import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wallet, Download, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { exportToCsv } from '@/lib/exportCsv';
import { toast } from 'sonner';

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

export default function DrawerAuditPage() {
  const [startDate, setStartDate] = useState(monthAgo());
  const [endDate, setEndDate] = useState(today());
  const [branchId, setBranchId] = useState('all');
  const [branches, setBranches] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate, limit: 200 };
      if (branchId && branchId !== 'all') params.branch_id = branchId;
      const { data } = await api.get('/cashier/drawer/events', { params });
      setEvents(data.events || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load drawer events'); }
    finally { setLoading(false); }
  }, [startDate, endDate, branchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cashOpens = events.filter((e) => e.opening_type === 'cash_sale').length;
  const manualOpens = events.length - cashOpens;

  const exportCsv = () => {
    if (!events.length) { toast.info('No drawer events to export'); return; }
    exportToCsv(`drawer_audit_${startDate}_${endDate}`, events, [
      { key: 'ts', label: 'Time', format: (v) => (v ? new Date(v).toLocaleString() : '') },
      { key: 'branch_name', label: 'Branch' },
      { key: 'opening_type', label: 'Opening Type' },
      { key: 'reason', label: 'Reason' },
      { key: 'transaction_number', label: 'Transaction #' },
      { key: 'cashier_email', label: 'Operator' },
    ]);
    toast.success(`Exported ${events.length} event(s)`);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="drawer-audit-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground flex items-center gap-3">
              <Wallet className="text-emerald-600" size={26} /> Cash Drawer Audit
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Every drawer opening with operator, transaction number and opening type — for shift reconciliation and loss prevention.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36" data-testid="drawer-start-date" />
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36" data-testid="drawer-end-date" />
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="w-44" data-testid="drawer-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={fetchData} data-testid="drawer-refresh"><RefreshCw size={14} className="mr-1" />Refresh</Button>
            <Button size="sm" onClick={exportCsv} data-testid="drawer-export"><Download size={14} className="mr-1" />Export</Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Openings', value: events.length },
            { label: 'Cash Sale', value: cashOpens },
            { label: 'Manual / No-Sale', value: manualOpens, warn: true },
          ].map((k, i) => (
            <Card key={i} data-testid={`drawer-kpi-${i}`}>
              <CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</div>
                <div className={`text-lg font-bold mt-1 ${k.warn && k.value > 0 ? 'text-amber-600' : 'text-foreground'}`}>{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drawer-audit-table">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    <th className="px-4 py-2.5">Time</th>
                    <th className="px-4 py-2.5">Branch</th>
                    <th className="px-4 py-2.5">Opening Type</th>
                    <th className="px-4 py-2.5">Reason</th>
                    <th className="px-4 py-2.5 text-right">Transaction #</th>
                    <th className="px-4 py-2.5">Operator</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
                  {!loading && events.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground" data-testid="drawer-empty">No drawer openings in this range.</td></tr>
                  )}
                  {!loading && events.map((e, i) => (
                    <tr key={e.id || i} className="border-b hover:bg-muted/20" data-testid={`drawer-row-${i}`}>
                      <td className="px-4 py-2.5 whitespace-nowrap">{e.ts ? new Date(e.ts).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2.5">{e.branch_name || '—'}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={e.opening_type === 'cash_sale' ? 'secondary' : 'outline'} className={e.opening_type === 'cash_sale' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {e.opening_type === 'cash_sale' ? 'Cash Sale' : 'Manual'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{e.reason || '—'}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{e.transaction_number ?? '—'}</td>
                      <td className="px-4 py-2.5 text-xs">{e.cashier_email || e.cashier_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
